import React from "react";
import "../reporttable.css";

const DashboardDataCard = ({
    data,
    formatAmount,
    loanColorOrder = [
        "indigo",
        "violet",
        "teal",
        "cyan",
        "pink",
        "amber",
    ],
    icons = [
        "fas fa-university",
        "fas fa-coins",
        "fas fa-sync",
        "fas fa-percentage",
        "fas fa-file-invoice",
        "fas fa-chart-line",
    ],
}) => {
    if (!data) return null;

    const totalNoOfLoans = Object.values(
        data.loanSummary || {}
    ).reduce(
        (sum, loan) =>
            sum + Number(loan.count || 0),
        0
    );

    const loanEntries = Object.entries(
        data.loanSummary || {}
    ).sort(([a], [b]) =>
        a.localeCompare(b)
    );

    return (
        <div className="jfmc-section">
            {/* =========================================
                JFMC TITLE
            ========================================= */}
            <h4 className="jfmc-title">
                {data.jfmcTypeName}
            </h4>
            {/* =========================================
                MAIN STAT CARDS
            ========================================= */}
            <div className="jfmc-row mt-3">
                {/* VFCs */}
                <div className="stat-card card-green stat-centered">
                    <div className="stat-icon-big icon-green">
                        <i className="fas fa-list"></i>
                    </div>
                    <div className="stat-title text-center">
                        {data.jfmcTypeName}'s
                    </div>
                    <div className="stat-value text-center">
                        {Number(
                            data.totalVfcs || 0
                        ).toLocaleString("en-IN")}
                    </div>
                    <div className="stat-sub text-center">
                        Active Committees
                    </div>
                </div>
                {/* Members */}
                <div className="stat-card card-blue stat-centered">
                    <div className="stat-icon-big icon-blue">
                        <i className="fas fa-users"></i>
                    </div>
                    <div className="stat-title text-center">
                        Members
                    </div>
                    <div className="stat-value text-center">
                        {Number(
                            data.totalMembers || 0
                        ).toLocaleString("en-IN")}
                    </div>
                    <div className="stat-sub text-center">
                        Registered Members
                    </div>
                </div>
                {/* SHGs */}
                <div className="stat-card card-orange stat-centered">
                    <div className="stat-icon-big icon-orange">
                        <i className="fas fa-layer-group"></i>
                    </div>
                    <div className="stat-title text-center">
                        SHGs
                    </div>
                    <div className="stat-value text-center">
                        {Number(
                            data.totalShgs || 0
                        ).toLocaleString("en-IN")}
                    </div>
                    <div className="stat-sub text-center">
                        Self Help Groups
                    </div>
                </div>
                {/* Number of Loans */}
                <div className="stat-card card-purple stat-centered">
                    <div className="stat-icon-big icon-purple">
                        <i className="fas fa-file-invoice"></i>
                    </div>
                    <div className="stat-title text-center">
                        Number of Loans
                    </div>
                    <div className="stat-value text-center">
                        {totalNoOfLoans.toLocaleString(
                            "en-IN"
                        )}
                    </div>
                    <div className="stat-sub text-center">
                        Active Loans
                    </div>
                </div>
                {/* Closing Balance */}
                <div className="stat-card card-green stat-centered">
                    <div className="stat-icon-big icon-green">
                        ₹
                    </div>
                    <div className="stat-title text-center">
                        Closing Balance as on{" "}
                        {data.closingDate
                            ? new Date(
                                data.closingDate
                            ).toLocaleDateString(
                                "en-GB",
                                {
                                    day: "2-digit",
                                    month: "short",
                                    year: "numeric",
                                }
                            )
                            : "N/A"}
                    </div>
                    <div className="stat-box-grid">
                        <div className="stat-mini-box">
                            <div className="mini-value">
                                ₹
                                {formatAmount(
                                    data.closingCash || 0
                                )}
                            </div>
                            <div className="mini-label">
                                Cash
                            </div>
                        </div>
                        <div className="stat-mini-box">
                            <div className="mini-value">
                                ₹
                                {formatAmount(
                                    data.closingBank || 0
                                )}
                            </div>
                            <div className="mini-label">
                                Bank
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {/* =========================================
                LOAN TYPE CARDS
            ========================================= */}

            {loanEntries.length > 0 && (
                <div className="loan-grid mt-3">
                    {loanEntries.map(([loanType, loan], index) => {
                        const color =
                            loanColorOrder[index % loanColorOrder.length];

                        return (
                            <div
                                key={loan.loanTypeId || loanType}
                                className={`stat-card card-${color} stat-centered`}
                            >
                                <div className={`stat-icon-big icon-${color}`}>
                                    <i className={icons[index % icons.length]}></i>
                                </div>

                                <div className="stat-title text-center">
                                    {loan.loanName || loanType}
                                </div>

                                <div className="stat-box-grid">
                                    {/* Loan Amount / Repayment */}
                                    <div className="stat-mini-box">
                                        <div className="mini-value">
                                            ₹{formatAmount(loan.amount || 0)}
                                        </div>

                                        <div className="mini-label">
                                            Loan Amount
                                        </div>
                                    </div>

                                    {/* Repayment Collections */}
                                    <div className="stat-mini-box">
                                        <div className="mini-value">
                                            {loan.collectionError ? (
                                                "Unavailable"
                                            ) : loan.collectionLoaded ? (
                                                `₹${formatAmount(loan.collections || 0)}`
                                            ) : (
                                                "Calculating..."
                                            )}
                                        </div>

                                        <div className="mini-label">
                                            Recovered
                                        </div>

                                        {!loan.collectionLoaded &&
                                            !loan.collectionError && (
                                                <i
                                                    className="fas fa-sync-alt fa-spin sync-status-icon"
                                                    title="Updating repayment collections"
                                                />
                                            )}
                                    </div>
                                </div>

                                {/* Loan Count */}
                                <div className="loan-count-text">
                                    {Number(loan.count || 0).toLocaleString("en-IN")} Loans
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

        </div>
    );
};

export default DashboardDataCard;